/**
 * PCA Orientation Analysis Tool
 * Analyzes element geometry to find principal axis using PCA
 * and visualizes it with a 3D arrow in Forge Viewer
 */

import { GeometryTools } from "@/services/core/geometryTools";
import { DEFAULT_PCA_CONFIG } from "@/types/orientation";
import type {
  OrientationResult,
  PCAConfig,
  Point2D,
  CovarianceMatrix,
  EigenPair,
  VertexExtractionResult,
  OrientationVisualization,
} from "@/types/orientation";

declare const Autodesk: any;

export class PCAOrientationTool {
  private static overlayScene = "pca-orientation-arrows";
  private static arrowCache = new Map<string, THREE.ArrowHelper>();

  /**
   * Main entry point - Analyze element and visualize orientation
   */
  static async analyzeAndVisualizeElementOrientation(
    dbId: number,
    viewer: Autodesk.Viewing.GuiViewer3D,
    config: Partial<PCAConfig> = {}
  ): Promise<OrientationResult | null> {
    try {
      // Merge with default config
      const fullConfig: PCAConfig = { ...DEFAULT_PCA_CONFIG, ...config };

      // Extract vertices from element
      const extraction = await this.extractVertices(viewer, dbId, fullConfig);
      if (!extraction || extraction.length < 3) {
        console.warn(`[PCA] Element ${dbId} has insufficient geometry`);
        return null;
      }

      // Project to 2D and compute PCA
      const points2D = this.projectToXY(extraction);
      const result = this.computePCA(
        points2D,
        dbId,
        GeometryTools.getElementBoundingBox(viewer, dbId)
      );

      if (!result) {
        console.warn(`[PCA] Could not compute orientation for element ${dbId}`);
        return null;
      }

      // Check confidence threshold
      if (result.confidence < (fullConfig.minConfidence || 0.3)) {
        console.warn(
          `[PCA] Element ${dbId} is too symmetric (confidence: ${result.confidence})`
        );
        return null;
      }

      // Get element name
      result.elementName = await GeometryTools.getElementName(viewer, dbId);

      // Create visualization
      this.createOrientationArrow(viewer, result, fullConfig);

      return result;
    } catch (error) {
      console.error("[PCA] Analysis failed:", error);
      return null;
    }
  }

  /**
   * Extract vertices from element geometry
   */
  private static extractVertices(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number,
    config: PCAConfig
  ): THREE.Vector3[] | null {
    try {
      // Use Forge Viewer's internal THREE.js instance to avoid version conflicts
      const THREE =
        (window as any).THREE || (viewer as any).impl?.renderer?.THREE;

      if (!THREE) {
        console.error("[PCA] THREE.js not available in Forge Viewer");
        return null;
      }

      const vertices: THREE.Vector3[] = [];
      const processedPositions = new Set<string>();
      const instanceTree = viewer.model.getInstanceTree();

      if (!instanceTree) {
        console.log(`[PCA] No instance tree available`);
        return null;
      }

      // Directly enumerate fragments and extract vertices
      instanceTree.enumNodeFragments(
        dbId,
        (fragId: number) => {
          try {
            const mesh = viewer.impl.getRenderProxy(viewer.model, fragId);

            if (mesh && mesh.geometry) {
              const geometry = mesh.geometry;
              const matrixWorld = mesh.matrixWorld || new THREE.Matrix4();

              // Try multiple ways to extract vertices
              let positions = null;
              let stride = 3;
              let count = 0;

              // Method 1: Direct position array
              if (geometry.attributes && geometry.attributes.position) {
                const posAttr = geometry.attributes.position;

                if (posAttr.array) {
                  positions = posAttr.array;
                  count = positions.length / 3;
                  stride = 3;
                } else if (
                  posAttr.count &&
                  typeof posAttr.getX === "function"
                ) {
                  // Method 2: Use getter functions
                  count = posAttr.count;
                  for (let i = 0; i < count; i++) {
                    const vertex = new THREE.Vector3(
                      posAttr.getX(i),
                      posAttr.getY(i),
                      posAttr.getZ(i)
                    );

                    // Transform to world coordinates
                    vertex.applyMatrix4(matrixWorld);

                    // Deduplicate
                    const key = `${vertex.x.toFixed(3)},${vertex.y.toFixed(3)},${vertex.z.toFixed(3)}`;
                    if (!processedPositions.has(key)) {
                      processedPositions.add(key);
                      vertices.push(vertex);
                    }
                  }
                  return; // Skip the position array processing below
                }
              }

              // Method 3: Check for vertex buffer
              if (!positions && geometry.vb) {
                positions = geometry.vb;
                stride = geometry.vbstride || 3;
                count = positions.length / stride;
              }

              // Process positions if we found them
              if (positions && count > 0) {
                for (let i = 0; i < count; i++) {
                  const vertex = new THREE.Vector3(
                    positions[i * stride],
                    positions[i * stride + 1],
                    positions[i * stride + 2]
                  );

                  // Transform to world coordinates
                  vertex.applyMatrix4(matrixWorld);

                  // Deduplicate
                  const key = `${vertex.x.toFixed(3)},${vertex.y.toFixed(3)},${vertex.z.toFixed(3)}`;
                  if (!processedPositions.has(key)) {
                    processedPositions.add(key);
                    vertices.push(vertex);
                  }
                }
              }
            }
          } catch (fragError) {
            console.warn(
              `[PCA] Error processing fragment ${fragId}:`,
              fragError
            );
          }
        },
        true
      );

      return vertices.length > 0 ? vertices : null;
    } catch (error) {
      console.error(`[PCA] Error in iterator extraction:`, error);
      return null;
    }
  }

  /**
   * Project 3D vertices to XY plane
   */
  private static projectToXY(vertices: THREE.Vector3[]): Point2D[] {
    return vertices.map((v) => ({ x: v.x, y: v.y }));
  }

  /**
   * Compute PCA for 2D points
   */
  private static computePCA(
    points: Point2D[],
    dbId: number,
    boundingBox: THREE.Box3
  ): OrientationResult | null {
    if (points.length < 3) return null;

    // Use Forge Viewer's internal THREE.js instance to avoid version conflicts
    const THREE = (window as any).THREE;

    if (!THREE) {
      console.error("[PCA] THREE.js not available");
      return null;
    }

    // Calculate centroid
    const centroid = points.reduce(
      (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
      { x: 0, y: 0 }
    );
    centroid.x /= points.length;
    centroid.y /= points.length;

    // Center the points
    const centered = points.map((p) => ({
      x: p.x - centroid.x,
      y: p.y - centroid.y,
    }));

    // Calculate covariance matrix
    const cov: CovarianceMatrix = {
      xx: 0,
      xy: 0,
      yx: 0,
      yy: 0,
    };

    for (const p of centered) {
      cov.xx += p.x * p.x;
      cov.xy += p.x * p.y;
      cov.yx += p.y * p.x;
      cov.yy += p.y * p.y;
    }

    const n = points.length;
    cov.xx /= n;
    cov.xy /= n;
    cov.yx /= n;
    cov.yy /= n;

    // Compute eigenvalues and eigenvectors (2x2 matrix)
    const eigenPairs = this.computeEigen2x2(cov);
    if (!eigenPairs) return null;

    // Sort by eigenvalue (descending)
    eigenPairs.sort((a, b) => b.value - a.value);

    // Calculate confidence (ratio of eigenvalues)
    const confidence =
      eigenPairs[1].value > 0
        ? 1 - eigenPairs[1].value / eigenPairs[0].value
        : 1;

    // Get principal direction
    const principalVector = eigenPairs[0].vector;
    const direction = new THREE.Vector3(
      principalVector.x,
      principalVector.y,
      0
    ).normalize();

    // Use bounding box center Z for arrow placement
    const centerZ = (boundingBox.min.z + boundingBox.max.z) / 2;
    const center = new THREE.Vector3(centroid.x, centroid.y, centerZ);

    return {
      center,
      direction,
      dbId,
      confidence,
      eigenvalues: {
        primary: eigenPairs[0].value,
        secondary: eigenPairs[1].value,
      },
      timestamp: new Date(),
    };
  }

  /**
   * Compute eigenvalues and eigenvectors for 2x2 matrix
   */
  private static computeEigen2x2(m: CovarianceMatrix): EigenPair[] | null {
    // For 2x2 matrix, we can use analytical solution
    // Characteristic equation: det(M - λI) = 0
    // λ² - (xx+yy)λ + (xx*yy - xy*yx) = 0

    const trace = m.xx + m.yy;
    const det = m.xx * m.yy - m.xy * m.yx;

    // Solve quadratic equation
    const discriminant = trace * trace - 4 * det;
    if (discriminant < 0) return null;

    const sqrtDisc = Math.sqrt(discriminant);
    const lambda1 = (trace + sqrtDisc) / 2;
    const lambda2 = (trace - sqrtDisc) / 2;

    // Find eigenvectors
    const eigenvectors: EigenPair[] = [];

    // For each eigenvalue, solve (M - λI)v = 0
    for (const lambda of [lambda1, lambda2]) {
      if (Math.abs(m.xy) > 1e-10) {
        // Use first row: (xx - λ)x + xy*y = 0
        const x = m.xy;
        const y = lambda - m.xx;
        const norm = Math.sqrt(x * x + y * y);
        eigenvectors.push({
          value: lambda,
          vector: { x: x / norm, y: y / norm },
        });
      } else if (Math.abs(m.yx) > 1e-10) {
        // Use second row: yx*x + (yy - λ)y = 0
        const x = lambda - m.yy;
        const y = m.yx;
        const norm = Math.sqrt(x * x + y * y);
        eigenvectors.push({
          value: lambda,
          vector: { x: x / norm, y: y / norm },
        });
      } else {
        // Diagonal matrix
        eigenvectors.push({
          value: lambda,
          vector: lambda === lambda1 ? { x: 1, y: 0 } : { x: 0, y: 1 },
        });
      }
    }

    return eigenvectors;
  }

  /**
   * Create and add arrow visualization to viewer
   */
  private static createOrientationArrow(
    viewer: Autodesk.Viewing.GuiViewer3D,
    result: OrientationResult,
    config: PCAConfig
  ): void {
    // Use Forge Viewer's internal THREE.js instance to avoid version conflicts
    const THREE =
      (window as any).THREE || (viewer as any).impl?.renderer?.THREE;

    if (!THREE) {
      console.error("[PCA] THREE.js not available in Forge Viewer");
      return;
    }

    // Create arrow helper
    const arrowLength = config.arrowLength || 5;
    const arrowColor = new THREE.Color(config.arrowColor || "#FF0000");
    const arrowZOffset = config.arrowZOffset || 0.5;

    // Adjust arrow origin Z position
    const origin = result.center.clone();
    origin.z += arrowZOffset;

    const arrow = new THREE.ArrowHelper(
      result.direction,
      origin,
      arrowLength,
      arrowColor,
      arrowLength * 0.3, // head length
      arrowLength * 0.15 // head width
    );

    // Create unique ID for this arrow
    const arrowId = `arrow-${result.dbId}-${Date.now()}`;

    // Clear any existing arrow for this element
    this.clearArrowsForElement(viewer, result.dbId);

    // Add to overlay scene
    if (!viewer.impl.overlayScenes[this.overlayScene]) {
      viewer.impl.createOverlayScene(this.overlayScene);
    }

    try {
      viewer.impl.addOverlay(this.overlayScene, arrow);
      viewer.impl.invalidate(true, true, true);

      // Cache arrow reference
      this.arrowCache.set(arrowId, arrow);
    } catch (error) {
      console.error("[PCA] Failed to add arrow overlay:", error);
      // Clean up the arrow object
      if (arrow.dispose) {
        arrow.dispose();
      }
    }
  }

  /**
   * Clear arrows for specific element
   */
  static clearArrowsForElement(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number
  ): void {
    const keysToRemove: string[] = [];

    this.arrowCache.forEach((arrow, key) => {
      if (key.includes(`arrow-${dbId}-`)) {
        viewer.impl.removeOverlay(this.overlayScene, arrow);
        keysToRemove.push(key);
      }
    });

    keysToRemove.forEach((key) => this.arrowCache.delete(key));

    if (keysToRemove.length > 0) {
      viewer.impl.invalidate(true, true, true);
    }
  }

  /**
   * Clear all arrows
   */
  static clearAllArrows(viewer: Autodesk.Viewing.GuiViewer3D): void {
    if (viewer.impl.overlayScenes[this.overlayScene]) {
      viewer.impl.clearOverlay(this.overlayScene);
      this.arrowCache.clear();
      viewer.impl.invalidate(true, true, true);
    }
  }

  /**
   * Create visualization with saved camera state
   */
  static createVisualization(
    result: OrientationResult,
    viewer: Autodesk.Viewing.GuiViewer3D
  ): OrientationVisualization {
    // Save current camera state
    const camera = viewer.navigation.getCamera();
    const viewState = {
      position: camera.position.clone(),
      target: camera.target.clone(),
      up: camera.up.clone(),
      fov: camera.fov,
    };

    return {
      result,
      arrowId: `arrow-${result.dbId}-${Date.now()}`,
      viewState,
      isVisible: true,
    };
  }

  /**
   * Focus view on element with arrow
   */
  static focusOnOrientation(
    visualization: OrientationVisualization,
    viewer: Autodesk.Viewing.GuiViewer3D,
    config: Partial<PCAConfig> = {}
  ): void {
    // Use Forge Viewer's internal THREE.js instance to avoid version conflicts
    const THREE =
      (window as any).THREE || (viewer as any).impl?.renderer?.THREE;

    if (!THREE) {
      console.error("[PCA] THREE.js not available in Forge Viewer");
      return;
    }

    // Clear previous highlights
    viewer.clearThemingColors();
    viewer.isolate([]);
    viewer.showAll();

    // Isolate and highlight element
    viewer.isolate([visualization.result.dbId]);
    viewer.setThemingColor(
      visualization.result.dbId,
      new THREE.Vector4(0, 0.5, 1, 0.6) // Blue highlight
    );

    // Recreate arrow if needed
    if (!visualization.isVisible) {
      this.createOrientationArrow(viewer, visualization.result, {
        ...DEFAULT_PCA_CONFIG,
        ...config,
      });
      visualization.isVisible = true;
    }

    // Calculate optimal camera position
    const center = visualization.result.center;
    const direction = visualization.result.direction;

    // Position camera perpendicular to the arrow direction for best view
    const cameraOffset = new THREE.Vector3(
      -direction.y * 10, // Perpendicular in XY plane
      direction.x * 10,
      8 // Elevated view
    );

    const cameraPosition = center.clone().add(cameraOffset);

    // Set view
    viewer.navigation.setView(cameraPosition, center);
    viewer.navigation.setVerticalFov(45);

    viewer.impl.invalidate(true, true, true);
  }

  /**
   * Get oriented bounding box with principal axes for an element
   * Used by ClearanceVolumeTools for accurate box expansion
   */
  static async getOrientedBoundingBox(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number
  ): Promise<{
    box: THREE.Box3;
    center: THREE.Vector3;
    orientation: OrientationResult;
    axes: {
      forward: THREE.Vector3;
      right: THREE.Vector3;
      up: THREE.Vector3;
    };
  } | null> {
    try {
      // Get element orientation using PCA
      const orientation = await this.analyzeAndVisualizeElementOrientation(
        dbId,
        viewer,
        { arrowLength: 0 } // Don't show arrow
      );

      if (!orientation) {
        return null;
      }

      // Get bounding box
      const box = GeometryTools.getElementBoundingBox(viewer, dbId);
      if (!box) {
        return null;
      }

      const THREE = (window as any).THREE;

      // Calculate axes based on orientation
      const forward = new THREE.Vector3(
        orientation.direction.x,
        orientation.direction.y,
        orientation.direction.z
      ).normalize();

      // Up is always Z-axis for building elements
      const up = new THREE.Vector3(0, 0, 1);

      // Right is cross product of up and forward
      const right = new THREE.Vector3().crossVectors(up, forward).normalize();

      // Recalculate forward to ensure orthogonality
      const adjustedForward = new THREE.Vector3().crossVectors(right, up).normalize();

      return {
        box,
        center: orientation.center,
        orientation,
        axes: {
          forward: adjustedForward,
          right,
          up
        }
      };
    } catch (error) {
      console.error("[PCA] Failed to get oriented bounding box:", error);
      return null;
    }
  }

  /**
   * Get vertices of an oriented bounding box
   * Returns 8 corner points of the box
   */
  static getBoxVertices(
    box: THREE.Box3,
    center?: THREE.Vector3
  ): THREE.Vector3[] {
    const THREE = (window as any).THREE;
    const vertices: THREE.Vector3[] = [];

    const min = box.min;
    const max = box.max;

    // 8 corners of the box
    vertices.push(new THREE.Vector3(min.x, min.y, min.z)); // 0: min corner
    vertices.push(new THREE.Vector3(max.x, min.y, min.z)); // 1
    vertices.push(new THREE.Vector3(max.x, max.y, min.z)); // 2
    vertices.push(new THREE.Vector3(min.x, max.y, min.z)); // 3
    vertices.push(new THREE.Vector3(min.x, min.y, max.z)); // 4
    vertices.push(new THREE.Vector3(max.x, min.y, max.z)); // 5
    vertices.push(new THREE.Vector3(max.x, max.y, max.z)); // 6: max corner
    vertices.push(new THREE.Vector3(min.x, max.y, max.z)); // 7

    return vertices;
  }

  /**
   * Get points on box sides for denser ray casting
   * Used when rayDensity is set to 'vertices-and-sides'
   */
  static getBoxSidePoints(
    box: THREE.Box3,
    pointsPerSide: number = 3
  ): THREE.Vector3[] {
    const THREE = (window as any).THREE;
    const points: THREE.Vector3[] = [];

    const min = box.min;
    const max = box.max;

    // Generate points on each face
    for (let i = 1; i < pointsPerSide - 1; i++) {
      const t = (i) / (pointsPerSide - 1);

      // Top face (z = max.z)
      points.push(new THREE.Vector3(
        min.x + (max.x - min.x) * t,
        min.y + (max.y - min.y) * 0.5,
        max.z
      ));

      // Bottom face (z = min.z)
      points.push(new THREE.Vector3(
        min.x + (max.x - min.x) * t,
        min.y + (max.y - min.y) * 0.5,
        min.z
      ));

      // Front face (y = max.y)
      points.push(new THREE.Vector3(
        min.x + (max.x - min.x) * 0.5,
        max.y,
        min.z + (max.z - min.z) * t
      ));

      // Back face (y = min.y)
      points.push(new THREE.Vector3(
        min.x + (max.x - min.x) * 0.5,
        min.y,
        min.z + (max.z - min.z) * t
      ));

      // Right face (x = max.x)
      points.push(new THREE.Vector3(
        max.x,
        min.y + (max.y - min.y) * t,
        min.z + (max.z - min.z) * 0.5
      ));

      // Left face (x = min.x)
      points.push(new THREE.Vector3(
        min.x,
        min.y + (max.y - min.y) * t,
        min.z + (max.z - min.z) * 0.5
      ));
    }

    return points;
  }

  /**
   * Get expansion target points for a given expansion distance
   * These are the points we'll ray cast towards to check for collisions
   */
  static getExpansionTargetPoints(
    box: THREE.Box3,
    expansionDistance: number,
    rayDensity: 'vertices' | 'vertices-and-sides' = 'vertices'
  ): {
    points: THREE.Vector3[];
    directions: Map<THREE.Vector3, string>;
  } {
    const THREE = (window as any).THREE;
    const expandedBox = box.clone();

    // Expand the box uniformly by the expansion distance
    expandedBox.min.x -= expansionDistance;
    expandedBox.min.y -= expansionDistance;
    expandedBox.min.z -= expansionDistance;
    expandedBox.max.x += expansionDistance;
    expandedBox.max.y += expansionDistance;
    expandedBox.max.z += expansionDistance;

    const points: THREE.Vector3[] = [];
    const directions = new Map<THREE.Vector3, string>();

    // Get vertices
    const vertices = this.getBoxVertices(expandedBox);
    points.push(...vertices);

    // Map vertices to their primary directions
    vertices.forEach((v, index) => {
      // Determine primary direction based on vertex position
      let dir = '';
      if (index < 4) dir = 'down';
      else dir = 'up';
      directions.set(v, dir);
    });

    // Add side points if requested
    if (rayDensity === 'vertices-and-sides') {
      const sidePoints = this.getBoxSidePoints(expandedBox, 3);
      points.push(...sidePoints);

      // Map side points to their directions
      sidePoints.forEach(p => {
        const center = new THREE.Vector3();
        box.getCenter(center);

        // Determine direction based on which face the point is on
        const dx = Math.abs(p.x - center.x);
        const dy = Math.abs(p.y - center.y);
        const dz = Math.abs(p.z - center.z);

        let dir = '';
        if (dz > dy && dz > dx) {
          dir = p.z > center.z ? 'up' : 'down';
        } else if (dy > dx) {
          dir = p.y > center.y ? 'front' : 'back';
        } else {
          dir = p.x > center.x ? 'right' : 'left';
        }

        directions.set(p, dir);
      });
    }

    return { points, directions };
  }
}
